import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/mobix/Logo";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "news", label: t.nav.news },
    { id: "showroom", label: t.nav.showroom },
  ];

  const goProduct = (path) => {
    setProductsOpen(false);
    setOpen(false);
    navigate(path);
    window.scrollTo({ top: 0 });
  };

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="mobix-navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 backdrop-blur-2xl bg-white/90 border-b border-mobix-mauve/20 shadow-sm"
    >
      <nav className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20 h-[72px] flex items-center justify-between">
        <button onClick={() => scrollToId("hero")} data-testid="nav-logo" className="flex items-center shrink-0">
          <Logo className="h-8 md:h-9 w-auto" />
        </button>

        <div className="hidden lg:flex items-center gap-8 lg:gap-10">
          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <button
              onClick={() => goProduct("/products")}
              data-testid="nav-link-products"
              aria-expanded={productsOpen}
              className="flex items-center gap-1 text-sm font-bold uppercase tracking-[0.12em] text-mobix-orange hover:text-[#d95f22] transition-colors duration-300"
            >
              {t.nav.products}
              <ChevronDown size={15} className={`transition-transform duration-300 ${productsOpen ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="nav-products-dropdown"
                  className="absolute left-0 top-full pt-3 w-64"
                >
                  <div className="rounded-3xl border border-mobix-mauve/20 bg-white/95 backdrop-blur-2xl p-2.5 shadow-[0_24px_60px_rgba(11,43,86,0.18)]">
                    {t.products.cards.map((card) => (
                      <button
                        key={card.key}
                        onClick={() => goProduct(`/products/${card.key}`)}
                        data-testid={`nav-products-item-${card.key}`}
                        className="block w-full text-left rounded-2xl px-4 py-2.5 text-sm font-semibold text-mobix-navy hover:bg-mobix-orange/10 hover:text-mobix-orange transition-colors duration-200"
                      >
                        {card.name}
                      </button>
                    ))}
                    <div className="my-1.5 h-px bg-mobix-mauve/15" />
                    <button
                      onClick={() => goProduct("/products")}
                      data-testid="nav-products-item-view-all"
                      className="block w-full text-left rounded-2xl px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-mobix-orange hover:bg-mobix-orange/10 transition-colors duration-200"
                    >
                      {t.products.viewAll}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {links.map((l, i) => (
            <button
              key={l.label + i}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-bold uppercase tracking-[0.12em] text-mobix-orange hover:text-[#d95f22] transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-mobix-orange group-hover:w-full transition-[width] duration-300" />
            </button>
          ))}

          <div className="flex items-center gap-1.5 rounded-full border border-mobix-mauve/30 bg-white/60 pl-3 pr-1 py-1" data-testid="lang-toggle">
            <Globe size={15} className="text-mobix-mauve" />
            {["bs", "en"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`lang-${code}`}
                className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase transition-colors duration-300 ${
                  lang === code ? "bg-mobix-navy text-white" : "text-mobix-mauve hover:text-mobix-navy"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            onClick={() => go("contact")}
            data-testid="nav-cta"
            className="px-6 py-2.5 rounded-3xl bg-mobix-orange text-white text-sm font-semibold shadow-lg shadow-mobix-orange/30 hover:scale-105 transition-transform duration-300"
          >
            {t.nav.cta}
          </button>
        </div>

        <button className="lg:hidden text-mobix-navy p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-mobix-mauve/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <div>
                <button
                  onClick={() => setMobileProductsOpen((v) => !v)}
                  data-testid="nav-mobile-products-toggle"
                  aria-expanded={mobileProductsOpen}
                  className="flex w-full items-center justify-between text-left text-lg font-bold uppercase tracking-[0.1em] text-mobix-orange"
                >
                  {t.nav.products}
                  <ChevronDown size={20} className={`transition-transform duration-300 ${mobileProductsOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {mobileProductsOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-3 pl-1 pt-4">
                        {t.products.cards.map((card) => (
                          <button
                            key={card.key}
                            onClick={() => goProduct(`/products/${card.key}`)}
                            data-testid={`nav-mobile-products-item-${card.key}`}
                            className="text-left text-base font-semibold text-mobix-navy"
                          >
                            {card.name}
                          </button>
                        ))}
                        <button
                          onClick={() => goProduct("/products")}
                          data-testid="nav-mobile-products-item-view-all"
                          className="text-left text-base font-bold uppercase tracking-wide text-mobix-orange"
                        >
                          {t.products.viewAll}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {links.map((l, i) => (
                <button key={l.label + i} onClick={() => go(l.id)} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`} className="text-left text-lg font-bold uppercase tracking-[0.1em] text-mobix-orange">
                  {l.label}
                </button>
              ))}
              <div className="flex items-center gap-1 rounded-full border border-mobix-mauve/30 p-1 w-fit">
                {["bs", "en"].map((code) => (
                  <button key={code} onClick={() => setLang(code)} className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase ${lang === code ? "bg-mobix-navy text-white" : "text-mobix-mauve"}`}>
                    {code}
                  </button>
                ))}
              </div>
              <button onClick={() => go("contact")} className="px-6 py-3 rounded-3xl bg-mobix-orange text-white text-sm font-semibold w-full">
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
